// Live 16:9 preview of a template + field values (docs/09 §B1). Debounces edits,
// POSTs to /templates/:id/preview, and drops the returned HTML into a sandboxed
// iframe via `srcDoc` — self-contained, no network, safe to render untrusted output
// (`sandbox="allow-scripts"`, no same-origin). Aborts the in-flight request when a
// newer edit arrives so the frame always reflects the latest values.

import { useEffect, useState } from 'react';
import { renderTemplatePreview, type TemplateFields } from '../../api/templates.ts';
import { ScaledFrame } from '../../components/ScaledFrame.tsx';

export function TemplateLivePreview({
  templateId,
  fields,
  debounceMs = 300,
}: {
  templateId: string;
  fields: TemplateFields;
  debounceMs?: number;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Re-run whenever the serialized fields change (stable across identical objects).
  const key = JSON.stringify(fields);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      renderTemplatePreview(templateId, fields, controller.signal)
        .then((h) => {
          setHtml(h);
          setLoading(false);
        })
        .catch((e) => {
          if (controller.signal.aborted) return;
          setError(true);
          setLoading(false);
          void e;
        });
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `key` is the serialized field values; templateId/debounceMs are primitive deps.
  }, [templateId, key, debounceMs]);

  return (
    <div className="tpl-preview">
      <div className="tpl-preview-stage">
        {html != null && <ScaledFrame title="Slide preview" srcDoc={html} />}
        {loading && <div className="tpl-preview-veil">Rendering…</div>}
        {error && !loading && (
          <div className="tpl-preview-veil error-text">Couldn’t render preview</div>
        )}
      </div>
    </div>
  );
}
